/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { ReactNode } from 'react'

// @mui material components
import Grid from '@mui/material/Grid'

// Material Dashboard 2 PRO React TS components
import brandPrimaryLogo from 'assets/images/mockingbird-logo-primary.png'
import brandPrimaryText from 'assets/images/mockingbird-primary-text.png'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { useMaterialUIController } from 'context'
import PageLayout from 'modules/LayoutContainers/PageLayout'

// Declaring props types for IllustrationLayout
interface Props {
	header?: ReactNode
	title?: string
	description?: string
	children: ReactNode
	illustration?: string
}

function IllustrationLayout({
	header,
	title,
	description,
	illustration,
	children,
}: Props): JSX.Element {
	const [controller] = useMaterialUIController()
	const { darkMode } = controller

	return (
		<PageLayout background="white">
			<Grid
				container
				sx={{
					backgroundColor: ({ palette: { background, white } }) =>
						darkMode ? background.default : white.main,
				}}
			>
				<Grid item xs={12} lg={6}>
					<MDBox
						display={{ xs: 'none', lg: 'flex' }}
						width="calc(100% - 2rem)"
						height="calc(100vh - 2rem)"
						borderRadius="lg"
						ml={2}
						mt={2}
						sx={{ backgroundImage: `url(${illustration})` }}
					/>
				</Grid>
				<Grid item xs={11} sm={8} md={6} lg={4} xl={3} sx={{ mx: 'auto' }}>
					<MDBox display="flex" flexDirection="column" justifyContent="center" height="75vh">
						<MDBox display={'flex'} flexDirection={'row'} justifyContent={'center'} my={10}>
							<MDBox component="img" src={brandPrimaryLogo} alt="Brand" width="5rem" />
							<MDBox component="img" src={brandPrimaryText} alt="Brand" width="20rem" />
						</MDBox>
						<MDBox py={3} px={3} textAlign="center">
							{!header ? (
								<>
									<MDBox mb={1} textAlign="center">
										<MDTypography variant="h4" fontWeight="bold">
											{title}
										</MDTypography>
									</MDBox>
									<MDTypography variant="body2" color="text">
										{description}
									</MDTypography>
								</>
							) : (
								header
							)}
						</MDBox>
						<MDBox p={3}>{children}</MDBox>
					</MDBox>
				</Grid>
			</Grid>
		</PageLayout>
	)
}

// Declaring default props for IllustrationLayout
IllustrationLayout.defaultProps = {
	header: '',
	title: '',
	description: '',
	illustration: '',
}

export default IllustrationLayout
